import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useToast } from '../../components/ui/Toast';
import { fetchTalentStats, searchTalent } from '../../api/talent';
import CandidateCard from '../components/CandidateCard';
import CandidateProfileModal from '../components/CandidateProfileModal';
import SkeletonCard from '../components/SkeletonCard';
import styles from './SearchPage.module.css';

const PAGE_TITLE = 'Samastix Talent Search — AI-Verified Candidates';

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const heroVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const filterVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, delay: 0.12, ease: 'easeOut' } },
};

function parseSkillsParam(raw) {
  if (!raw?.trim()) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function buildSearchParams(skills, minExperience, minRating, locationFilter) {
  const params = new URLSearchParams();
  if (skills.length > 0) params.set('skills', skills.join(','));
  if (minExperience > 0) params.set('minExperience', String(minExperience));
  if (minRating > 0) params.set('minRating', String(minRating));
  if (locationFilter.trim()) params.set('location', locationFilter.trim());
  return params;
}

function SkillsTagInput({ skills, onChange }) {
  const [draft, setDraft] = useState('');

  const addSkill = (raw) => {
    const value = raw.trim();
    if (!value) return;
    if (!skills.some((s) => s.toLowerCase() === value.toLowerCase())) {
      onChange([...skills, value]);
    }
    setDraft('');
  };

  return (
    <motion.div className={styles.skillsField}>
      {skills.map((skill, index) => (
        <motion.span key={`${skill}-${index}`} className={styles.skillTag} layout>
          {skill}
          <button
            type="button"
            className={styles.skillTagRemove}
            onClick={() => onChange(skills.filter((_, i) => i !== index))}
            aria-label={`Remove ${skill}`}
          >
            ×
          </button>
        </motion.span>
      ))}
      <input
        className={styles.skillInput}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addSkill(draft.replace(/,/g, ''));
          } else if (e.key === 'Backspace' && !draft && skills.length > 0) {
            onChange(skills.slice(0, -1));
          }
        }}
        onBlur={() => draft.trim() && addSkill(draft)}
        placeholder={skills.length === 0 ? 'Add skills (e.g. Java, React, AWS)...' : ''}
        aria-label="Add skill"
      />
    </motion.div>
  );
}

function readFiltersFromParams(searchParams) {
  const urlMinExp = Number(searchParams.get('minExperience') || 0);
  const urlMinRating = Number(searchParams.get('minRating') || 0);
  return {
    skills: parseSkillsParam(searchParams.get('skills')),
    minExperience: !Number.isNaN(urlMinExp) && urlMinExp > 0 ? urlMinExp : 0,
    minRating: !Number.isNaN(urlMinRating) && urlMinRating > 0 ? urlMinRating : 0,
    locationFilter: searchParams.get('location') || '',
  };
}

export default function SearchPage() {
  const routerLocation = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();

  const [skills, setSkills] = useState(() => readFiltersFromParams(searchParams).skills);
  const [minExperience, setMinExperience] = useState(
    () => readFiltersFromParams(searchParams).minExperience,
  );
  const [minRating, setMinRating] = useState(() => readFiltersFromParams(searchParams).minRating);
  const [locationFilter, setLocationFilter] = useState(
    () => readFiltersFromParams(searchParams).locationFilter,
  );
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [poolTotal, setPoolTotal] = useState(0);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);

  useEffect(() => {
    document.title = PAGE_TITLE;
    return () => {
      document.title = 'Samastix';
    };
  }, []);

  useEffect(() => {
    fetchTalentStats()
      .then((data) => setPoolTotal(data.totalCandidates ?? 0))
      .catch(() => setPoolTotal(0));
  }, []);

  const syncUrl = useCallback(() => {
    const next = buildSearchParams(skills, minExperience, minRating, locationFilter);
    setSearchParams(next, { replace: true });
  }, [skills, minExperience, minRating, locationFilter, setSearchParams]);

  const runSearch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: 0, size: 20 };
      if (skills.length > 0) params.skills = skills.join(',');
      if (minExperience > 0) params.minExperience = minExperience;
      if (minRating > 0) params.minRating = minRating * 10;
      if (locationFilter.trim()) params.location = locationFilter.trim();

      const data = await searchTalent(params);
      setResults(data);
      setTotal(data.length);
      setHasSearched(true);
    } catch (err) {
      setResults([]);
      setTotal(0);
      setHasSearched(true);
      setError(err?.message || 'Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [skills, minExperience, minRating, locationFilter]);

  useEffect(() => {
    syncUrl();
    const timer = window.setTimeout(runSearch, 500);
    return () => window.clearTimeout(timer);
  }, [runSearch, syncUrl]);

  useEffect(() => {
    if (routerLocation.state?.signupSuccess) {
      toast.success('Account created! Start searching.');
      window.history.replaceState({}, document.title);
    }
  }, [routerLocation.state, toast]);

  const clearFilters = () => {
    setSkills([]);
    setMinExperience(0);
    setMinRating(0);
    setLocationFilter('');
    setSearchParams({}, { replace: true });
  };

  const handleShareSearch = async () => {
    const qs = buildSearchParams(skills, minExperience, minRating, locationFilter).toString();
    const url = `${window.location.origin}/recruiter/search${qs ? `?${qs}` : ''}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Search link copied to clipboard');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const statsLabel = useMemo(
    () => poolTotal.toLocaleString(),
    [poolTotal],
  );

  const showEmpty = hasSearched && !loading && results.length === 0 && !error;

  return (
    <motion.div className={styles.page} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <motion.header className={styles.hero} variants={heroVariants} initial="hidden" animate="show">
        <motion.h1 className={styles.heroTitle}>Find Verified Talent</motion.h1>
        <motion.p className={styles.heroSubtitle}>
          Every candidate pre-screened by AI. Rated by performance, not resume.
        </motion.p>
      </motion.header>

      <motion.div
        className={styles.statsBar}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
      >
        <span className={styles.statPill}>
          <span className={`${styles.statDot} ${styles.dotCyan}`} aria-hidden="true" />
          {statsLabel} Candidates
        </span>
        <span className={styles.statPill}>
          <span className={`${styles.statDot} ${styles.dotTeal}`} aria-hidden="true" />
          AI-Verified Scores
        </span>
        <span className={styles.statPill}>
          <span className={`${styles.statDot} ${styles.dotGreen}`} aria-hidden="true" />
          Updated Daily
        </span>
      </motion.div>

      <motion.form
        className={styles.filterCard}
        variants={filterVariants}
        initial="hidden"
        animate="show"
        onSubmit={(e) => {
          e.preventDefault();
          runSearch();
        }}
      >
        <div className={styles.filterToolbar}>
          <button type="button" className={styles.shareBtn} onClick={handleShareSearch}>
            Share this search
          </button>
        </div>

        <div className={styles.filterRow}>
          <SkillsTagInput skills={skills} onChange={setSkills} />

          <div className={styles.slidersGroup}>
            <motion.div className={styles.sliderBlock}>
              <label className={styles.sliderLabel} htmlFor="min-experience">
                Min Experience
              </label>
              <input
                id="min-experience"
                type="range"
                min={0}
                max={15}
                step={1}
                value={minExperience}
                onChange={(e) => setMinExperience(Number(e.target.value))}
                className={styles.slider}
              />
              <span className={styles.sliderValue}>{minExperience} yrs</span>
            </motion.div>
            <div className={styles.sliderBlock}>
              <label className={styles.sliderLabel} htmlFor="min-rating">
                Min Rating
              </label>
              <input
                id="min-rating"
                type="range"
                min={0}
                max={10}
                step={0.5}
                value={minRating}
                onChange={(e) => setMinRating(Number(e.target.value))}
                className={styles.slider}
              />
              <span className={styles.sliderValue}>
                ★ {minRating % 1 === 0 ? minRating : minRating.toFixed(1)}
              </span>
            </div>
          </div>

          <div className={styles.locationField}>
            <input
              className={styles.glassInput}
              type="text"
              placeholder="Location or Remote"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              aria-label="Location"
            />
          </div>

          <div className={styles.searchActions}>
            <motion.button
              type="submit"
              className={styles.searchBtn}
              disabled={loading}
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
            >
              {loading ? 'Searching…' : 'Search'}
            </motion.button>
          </div>
        </div>
      </motion.form>

      <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
        {error && (
          <div className={styles.errorBanner} role="alert">
            {error}
          </div>
        )}

        {!loading && hasSearched && results.length > 0 && (
          <p className={styles.resultsHeader}>
            {total} candidate{total === 1 ? '' : 's'} found
          </p>
        )}

        {loading ? (
          <div className={styles.grid}>
            {Array.from({ length: 6 }, (_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
              >
                <SkeletonCard />
              </motion.div>
            ))}
          </div>
        ) : showEmpty ? (
          <motion.div
            className={styles.emptyState}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <motion.div className={styles.emptyIcon} aria-hidden="true">
              🔍
            </motion.div>
            <p className={styles.emptyTitle}>No candidates match these filters</p>
            <p className={styles.emptySub}>
              Try broadening your skills list or lowering the minimum rating
            </p>
            <button type="button" className={styles.clearBtn} onClick={clearFilters}>
              Clear filters
            </button>
          </motion.div>
        ) : results.length > 0 ? (
          <motion.div
            className={styles.grid}
            variants={containerVariants}
            initial="hidden"
            animate="show"
            key={results.map((r) => r.id).join('-')}
          >
            {results.map((profile) => (
              <CandidateCard
                key={profile.id}
                profile={profile}
                highlightSkills={skills}
                onSelect={setSelectedProfile}
              />
            ))}
          </motion.div>
        ) : null}
      </motion.section>

      <CandidateProfileModal
        profile={selectedProfile}
        onClose={() => setSelectedProfile(null)}
      />
    </motion.div>
  );
}
